import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

export interface WorkflowStep {
  id: number
  label: string
  role: string
}

export const WORKFLOW_STEPS: WorkflowStep[] = [
  { id: 1, label: 'Crea', role: 'Operativo' },
  { id: 2, label: 'Produci', role: 'Copywriter / Designer' },
  { id: 3, label: 'Approva', role: 'Manager' },
  { id: 4, label: 'Pubblica', role: 'Social Manager' },
  { id: 5, label: 'Pubblicato', role: '' },
]

interface WorkflowStepperProps {
  currentStep: number
  className?: string
  onStepClick?: (stepId: number) => void
  steps?: WorkflowStep[]
}

export function WorkflowStepper({ currentStep, className, onStepClick, steps = WORKFLOW_STEPS }: WorkflowStepperProps) {
  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-start gap-0">
        {steps.map((step, idx) => {
          const isCompleted = step.id < currentStep
          const isCurrent = step.id === currentStep
          const isFuture = step.id > currentStep
          const isLast = idx === steps.length - 1
          // Solo lo step successivo è cliccabile (avanza solo in avanti, uno alla volta)
          const isNextStep = step.id === currentStep + 1
          const isClickable = !!onStepClick && isNextStep

          return (
            <div key={step.id} className="flex-1 flex flex-col items-center relative">
              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn(
                    'absolute top-3 left-1/2 h-0.5 w-full',
                    isCompleted ? 'bg-success-500' : 'bg-slate-200'
                  )}
                />
              )}

              {/* Step circle */}
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(step.id)}
                className={cn(
                  'relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all',
                  isCompleted && 'bg-success-500 border-success-500',
                  isCurrent && 'bg-white border-accent-500 ring-2 ring-accent-500/20',
                  isFuture && 'bg-white border-slate-200',
                  isClickable && 'cursor-pointer hover:scale-110 hover:shadow-md hover:border-accent-300 hover:bg-accent-50',
                )}
              >
                {isCompleted ? (
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                ) : (
                  <span
                    className={cn(
                      'text-[9px] font-bold',
                      isCurrent ? 'text-accent-600' : 'text-slate-400'
                    )}
                  >
                    {step.id}
                  </span>
                )}
              </button>

              {/* Step label */}
              <div
                className={cn('mt-1.5 text-center px-0.5', isClickable && 'cursor-pointer')}
                onClick={() => isClickable && onStepClick?.(step.id)}
              >
                <p
                  className={cn(
                    'text-[9px] font-semibold leading-tight',
                    isCompleted && 'text-success-600',
                    isCurrent && 'text-accent-600',
                    isFuture && 'text-slate-400'
                  )}
                >
                  {step.label}
                </p>
                <p className="text-[8px] text-slate-400 leading-tight mt-0.5 hidden sm:block">
                  {step.role}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
